/**
 * landingPages — the merged set of SEO landing pages.
 *
 * Content lives in two JSON files only so neither becomes unmanageable to
 * edit; they are one logical list. Everything consumes this module rather
 * than the raw JSON, so adding a third file later changes one line here
 * and nothing else.
 *
 * api/ssr.js and api/sitemap.js read the JSON directly (they are
 * CommonJS), which is why the merge is a plain concatenation with no
 * transformation — the two sides must see identical data.
 */

import base from "./landingPages.json";
import extra from "./landingPages-extra.json";
import products from "./landingPages-products.json";
import hr from "./landingPages-hr.json";

export const pages = [
  ...base.pages,
  ...extra.pages,
  ...products.pages,
  ...hr.pages,
];

export const slugs = pages.map((p) => p.slug);

export function getPage(slug) {
  return pages.find((p) => p.slug === slug) || null;
}

/**
 * Human-readable label for a page, used in breadcrumbs and related links.
 * Derived from the slug so a new page never silently shows a raw slug,
 * with overrides where the derived form reads badly.
 */
const LABEL_OVERRIDES = {
  services: "Services",
  about: "About",
  "full-stack-developer": "The Full Stack Developer Role",
  "mern-stack-developer": "The MERN Stack",
  "technical-lead": "The Technical Lead Role",
  "saas-development": "SaaS Development",
  "api-development": "API Development",
  "web-development": "Web Development",
  "3d-web-development": "3D Web Development",
  "frontend-development": "Frontend Development",
  "backend-development": "Backend Development",
  "hire-nodejs-developer": "Hire a Node.js Developer",
  "hire-mern-stack-developer": "Hire a MERN Stack Developer",
  "hire-mongodb-developer": "Hire a MongoDB Developer",
  "hire-nextjs-developer": "Hire a Next.js Developer",
  "hire-ai-developer": "Hire an AI Integration Developer",
  "hire-remote-developer": "Hire a Remote Developer",
  "custom-software-development": "Custom Software Development",
  "business-management-software": "Business Management Software",
  "hr-management-software": "HR Management Software",
  "ecommerce-website-development": "E-Commerce Development",
  "hr-automation-system": "HR Automation System",
  "ai-hr-management-system": "AI HR Management",
};

export function labelOf(page) {
  const slug = typeof page === "string" ? page : page.slug;
  if (LABEL_OVERRIDES[slug]) return LABEL_OVERRIDES[slug];

  const words = slug.split("-");
  if (words[0] === "hire") {
    const rest = words.slice(1).join(" ");
    const article = /^[aeiou]/i.test(rest) ? "an" : "a";
    return `Hire ${article} ${rest.replace(/\b\w/g, (c) => c.toUpperCase())}`;
  }
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Breadcrumb trail, derived from where the page sits in the hierarchy. */
export function trailOf(page) {
  const home = { name: "Home", url: "/" };
  if (page.kind === "hub" || page.kind === "about") {
    return [home, { name: labelOf(page), url: `/${page.slug}` }];
  }
  return [
    home,
    { name: "Services", url: "/services" },
    { name: labelOf(page), url: `/${page.slug}` },
  ];
}

const api = { pages, slugs, getPage, labelOf, trailOf };
export default api;
